import { useState, useEffect, createContext, useContext } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate,useLocation } from "react-router-dom";
import Landing from "./pages/Landing";
import Login    from "./pages/Login";       // keep — used internally
import AboutPage    from "./pages/AboutPage";
import PlatformPage from "./pages/PlatformPage";
import Signup from "./pages/Signup";
import Onboarding from "./pages/Onboarding";
import DailyEmailPage from "./pages/DailyEmailPage";
import Dashboard from "./pages/Dashboard";
import { Toaster } from "sonner";
import { supabase } from "./supabaseClient";
import { logLogout, logPageView } from "./utils/activityLogger";
import AdminDashboard from "./pages/AdminDashboard";
import { PlanProvider } from "./hooks/usePlan";
import OTPVerify       from "./pages/OTPVerify";
import PendingApproval from "./pages/PendingApproval";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword  from "./pages/ResetPassword";

export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const logout = async () => {
     logLogout();
    await supabase.auth.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0b0f]">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return user ? children : <Navigate to="/login" />;
};
function PageTracker() {
  const location = useLocation();
  const PAGE_NAMES = {
    '/dashboard':           'Dashboard',
    '/market-intelligence': 'Market Intelligence',
    '/hedge-solutions':     'Hedge Solutions',
    '/investments':         'Investments',
    '/debt-management':     'Debt Management',
    '/receivables':         'Receivables',
    '/payables':            'Payables',
    '/daily-email':         'Daily Email',
  };
  useEffect(() => {
    const name = PAGE_NAMES[location.pathname];
    if (name) logPageView(name);
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PageTracker />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/about"    element={<AboutPage />} />      {/* ← new */}
  <Route path="/platform" element={<PlatformPage />} />   {/* ← new */}
            <Route path="/daily-email" element={<DailyEmailPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/admin" element={
  process.env.NODE_ENV === 'production' 
    ? <Navigate to="/login" /> 
    : <AdminDashboard />
} />

            <Route path="/forgot-password" element={<ForgotPassword />} />
<Route path="/reset-password"  element={<ResetPassword />} />
            <Route path="/verify-otp"       element={<OTPVerify />} />
<Route path="/pending-approval" element={<PendingApproval />} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><PlanProvider><Dashboard /></PlanProvider></ProtectedRoute>} />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;